// Registered voters — the roll's IEBC tree: County > Constituency > Ward >
// Polling centre, by date of registration. Order (per the design): filters,
// pivot, tribe, summary table.

import React, { useMemo, useState } from 'react';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { areaName, daysBetween, periodLabels, useDataScope } from './data';
import {
  DEMOGRAPHIC, emptyIebc, IEBC_LABELS, IEBC_LEVELS, iebcChildLevel, IebcFilter, IebcLevel, isVotingOnly, MAX_DAY_COLUMNS,
  PIVOT_BY_LABELS, PivotBy, RegRow, useIebcOptions, useRegPivot, useRegRows,
} from './agg';
import { PivotTable } from './PivotTable';
import { periodText } from './IdSections';
import {
  allDates, C, Column, DataTable, DateFilter, DateFilterValue, Dropdown, FilterBar, fmt, fmtShort, Kpi, Panel, PendingData, Section, Toggle,
} from './ui';

/** 'county' -> 'counties', 'ward' -> 'wards', 'embassy / consulate' -> 'embassies / consulates'. */
const plural = (w: string) => w.split(' / ').map((x) => (/[^aeiou]y$/.test(x) ? `${x.slice(0, -1)}ies` : `${x}s`)).join(' / ');

const tribeColors = () => [C.green, C.blue, C.yellow, C.orange, C.purple, C.cyan, '#E8684A', '#6DC8EC', '#9270CA', '#FF9D4D', '#269A99', '#FF99C3', '#5B8FF9', '#8b95a5'];
const ALL: DateFilterValue = { preset: 'all', from: '', to: '' };

const Loading: React.FC<{ on: boolean }> = ({ on }) =>
  on ? <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.green } }} /> : null;

const LevelDropdown: React.FC<{ level: IebcLevel; f: IebcFilter; set: (l: IebcLevel, v: string) => void }> = ({ level, f, set }) => {
  const { values, loading } = useIebcOptions(level, f);
  const i = IEBC_LEVELS.indexOf(level);
  const ready = IEBC_LEVELS.slice(0, i).every((l) => f[l]);
  const label = IEBC_LABELS[level];
  return (
    <Dropdown label={label} value={f[level]} onChange={(v) => set(level, v)}
      options={values.map((v) => ({ value: v, label: areaName(v) }))}
      placeholder={!ready ? `Pick a ${IEBC_LABELS[IEBC_LEVELS[i - 1]].toLowerCase()} first` : loading ? 'Loading…' : `All ${plural(label.toLowerCase())}`}
      disabled={!ready} />
  );
};

export const RegisteredSection: React.FC<{ county: string }> = ({ county }) => {
  const scopeData = useDataScope();
  const [f, setF] = useState<IebcFilter>({ ...emptyIebc, county });
  const [dates, setDates] = useState<DateFilterValue>(ALL);
  const [pivotRows, setPivotRows] = useState<IebcLevel | ''>('');
  const [pivotBy, setPivotBy] = useState<PivotBy>('month');
  const [tribeView, setTribeView] = useState('chart');

  React.useEffect(() => setF({ ...emptyIebc, county }), [county]);

  const first = scopeData.first;
  const last = scopeData.last;
  const days = dates.preset === 'all' || !first ? Infinity : daysBetween(dates.from, dates.to);
  // a day-by-day pivot only while it stays readable
  const timeBy: PivotBy = pivotBy === 'day' && days > MAX_DAY_COLUMNS ? 'week' : pivotBy;
  const level = iebcChildLevel(f);
  let rowsBy: IebcLevel = pivotRows || level;
  // sex, age and tribe go down to ward
  if (DEMOGRAPHIC.includes(timeBy) && rowsBy === 'pollingCentre') rowsBy = 'ward';
  const reg = useRegRows(f, dates);
  const pivot = useRegPivot(f, dates, rowsBy, timeBy, !(timeBy === 'week' && days > 7 * 120));
  // tribe totals: the tribe pivot at the level above, summed
  const tribeRowsBy: IebcLevel = level === 'pollingCentre' ? 'ward' : level;
  const tribePivot = useRegPivot(f, dates, tribeRowsBy, 'tribe');

  const rows = reg.rows;
  const total = rows.reduce((s, r) => s + r.registered, 0);
  const centres = rows.reduce((s, r) => s + r.centres, 0);
  // polling-centre rows repeat their ward's demographics: use the ward's once
  const demoSource = level === 'pollingCentre' && rows.length ? [rows[0]] : rows;
  const dFemale = demoSource.reduce((s, r) => s + r.female, 0);
  const dMale = demoSource.reduce((s, r) => s + r.male, 0);
  const dYouth = demoSource.reduce((s, r) => s + r.youth, 0);
  const dAge = demoSource.reduce((s, r) => s + r.age_recorded, 0);

  const tribes = useMemo(() => {
    const p = tribePivot.pivot;
    if (!p) return [];
    return p.columns
      .map((c, j) => ({ tribe: c, registered: p.rows.reduce((s, r) => s + r.values[j], 0) }))
      .sort((a, b) => (a.tribe === 'Not recorded' ? 1 : b.tribe === 'Not recorded' ? -1 : b.registered - a.registered));
  }, [tribePivot.pivot]);
  const tribeTotal = tribes.reduce((s, t) => s + t.registered, 0);

  const set = (lvl: IebcLevel, v: string) => {
    const i = IEBC_LEVELS.indexOf(lvl);
    const next = { ...f, [lvl]: v };
    IEBC_LEVELS.slice(i + 1).forEach((l) => { next[l] = ''; });
    setF(next);
    setPivotRows('');
  };
  const drill = (key: string) => {
    if (f.pollingCentre) return;
    // polling-centre rows are labelled "Centre · Ward" when no ward is chosen
    set(level, key);
  };
  // pivot rows can be any level at or below the current drill level
  // (sex, age and tribe stop at ward, so a chosen ward still offers itself)
  const topRowLevel: IebcLevel = DEMOGRAPHIC.includes(timeBy) && level === 'pollingCentre' ? 'ward' : level;
  const pivotRowOptions = IEBC_LEVELS.slice(IEBC_LEVELS.indexOf(topRowLevel))
    .filter((l) => !(DEMOGRAPHIC.includes(timeBy) && l === 'pollingCentre'))
    .map((l) => ({ value: l, label: IEBC_LABELS[l] }));
  const pivotColOptions = (['month', 'week', 'day', 'year', 'sex', 'age', 'tribe'] as PivotBy[])
    .filter((k) => k !== 'day' || days <= MAX_DAY_COLUMNS)
    .filter((k) => k !== 'week' || days <= 7 * 120)
    .map((k) => ({ value: k, label: PIVOT_BY_LABELS[k] }));

  const allColumns: Column<RegRow>[] = [
    { key: 'name', label: IEBC_LABELS[level], value: (r) => areaName(r.name) },
    { key: 'registered', label: 'Registered', value: (r) => r.registered, kind: 'num', color: C.green },
    { key: 'share', label: 'Share', value: (r) => (total ? (r.registered / total) * 100 : 0), kind: 'percent', total: () => 100 },
    { key: 'male', label: 'Male', value: (r) => r.male, kind: 'num', total: () => dMale },
    { key: 'femaleN', label: 'Female', value: (r) => r.female, kind: 'num', total: () => dFemale },
    { key: 'female', label: 'Female %', value: (r) => r.pctFemale, kind: 'percent', total: () => (dMale + dFemale ? (dFemale / (dMale + dFemale)) * 100 : 0) },
    { key: 'youth', label: 'Youth 18–34', value: (r) => r.pctYouth, kind: 'percent', total: () => (dAge ? (dYouth / dAge) * 100 : 0) },
    { key: 'centres', label: 'Polling centres', value: (r) => r.centres, kind: 'num', optional: level === 'pollingCentre' },
  ];
  // Sex and age are recorded per ward: polling-centre rows would only repeat
  // their ward's figures, so those columns go (the KPIs show the ward's).
  const DEMO_COLS = ['male', 'femaleN', 'female', 'youth'];
  const columns = level === 'pollingCentre' ? allColumns.filter((c) => !DEMO_COLS.includes(c.key)) : allColumns;

  const scope = areaName(f.pollingCentre || f.ward || f.constituency || f.county) || 'Kenya';
  const dateValue = dates.preset === 'all' && first ? allDates(first, last) : dates;
  const pivotRowsData = (pivot.pivot?.rows ?? []).map((r) => ({ key: r.key, label: areaName(r.key), values: r.values }));
  // time columns: the latest period reads "To date"
  const pivotColumns = DEMOGRAPHIC.includes(timeBy) ? (pivot.pivot?.columns ?? []) : periodLabels(pivot.pivot?.columns ?? [], timeBy);
  const monthlyNote = DEMOGRAPHIC.includes(timeBy)
    ? ` · ${PIVOT_BY_LABELS[timeBy].toLowerCase()} recorded per ward, dates by whole month`
    : '';

  return (
    <Section id="registered" icon={<HowToVoteIcon />} title="Registered voters" accent={C.green}
      subtitle={`${scope} · registered ${periodText(dates)} · IEBC voter roll`}>
      <FilterBar onClear={() => { setF(emptyIebc); setDates(ALL); setPivotRows(''); }}>
        {IEBC_LEVELS.map((l) => <LevelDropdown key={l} level={l} f={f} set={set} />)}
        {first && <DateFilter label="Date of registration" value={dateValue} onChange={setDates} first={first} last={last} />}
      </FilterBar>

      {isVotingOnly(f.county) ? (
        <PendingData area={areaName(f.county)} onBack={() => set('county', '')} />
      ) : (<>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi label="Registered voters" value={reg.loading ? '…' : total} color={C.green} sub={`as per area of voting · ${periodText(dates)}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Female" value={reg.loading ? '…' : `${(dMale + dFemale ? (dFemale / (dMale + dFemale)) * 100 : 0).toFixed(1)}%`} color={C.purple} sub={`${fmt(dFemale)} voters${level === 'pollingCentre' ? ' in the ward' : ''}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Youth 18–34" value={reg.loading ? '…' : `${(dAge ? (dYouth / dAge) * 100 : 0).toFixed(1)}%`} color={C.yellow} sub={`${fmt(dYouth)} voters${level === 'pollingCentre' ? ' in the ward' : ''}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Polling centres" value={reg.loading ? '…' : centres} color={C.cyan} sub={`in ${scope}`} /></Grid>
      </Grid>

      {/* 1. Pivot */}
      <Panel sx={{ height: 560, mb: 2 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mr: 1, mt: 2.5 }}>Pivot</Typography>
            <Dropdown label="Rows" value={rowsBy} options={pivotRowOptions} clearable={false}
              onChange={(v) => setPivotRows((v || '') as IebcLevel | '')} minWidth={170} />
            <Dropdown label="Columns" value={timeBy} options={pivotColOptions} clearable={false}
              onChange={(v) => setPivotBy((v || 'month') as PivotBy)} minWidth={170} />
            <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 3 }}>
              Registered voters · {periodText(dates)}{monthlyNote}{pivotBy === 'day' && timeBy !== 'day' ? ' · pick 2 months or less for daily columns' : ''}
            </Typography>
          </Box>
        }>
        <Loading on={pivot.fetching} />
        {pivot.error ? (
          <Typography sx={{ p: 2, color: C.orange, fontSize: '0.8rem' }}>{(pivot.error as any)?.message || 'Could not load the pivot.'}</Typography>
        ) : (
          <PivotTable rowHeader={IEBC_LABELS[rowsBy]} columns={pivotColumns} rows={pivotRowsData} maxHeight={430}
            onRowClick={rowsBy === level && !f.pollingCentre ? drill : undefined} fileName={`pivot-${rowsBy}-by-${timeBy}`} />
        )}
      </Panel>

      <Grid container spacing={2}>
        {/* 2. Tribe */}
        <Grid item xs={12} lg={5}>
          <Panel title={`By tribe${level === 'pollingCentre' ? ' · whole ward' : ''}`} sx={{ height: 480 }} actions={
            <Toggle value={tribeView} onChange={setTribeView} options={[{ value: 'chart', label: 'Chart' }, { value: 'table', label: 'Table' }]} />
          }>
            <Loading on={tribePivot.fetching} />
            {tribeView === 'chart' ? (
              <Box sx={{ height: 420, p: 1 }}>
                <ResponsiveContainer>
                  <BarChart data={tribes.slice(0, 15)} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} />
                    <YAxis type="category" dataKey="tribe" width={96} tick={{ fill: C.text, fontSize: 11 }} interval={0} />
                    <Tooltip contentStyle={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                      cursor={{ fill: C.faint }}
                      formatter={(v: number) => [`${v.toLocaleString()} (${(tribeTotal ? (v / tribeTotal) * 100 : 0).toFixed(1)}%)`, 'Registered']} />
                    <Bar dataKey="registered" radius={[0, 3, 3, 0]}>
                      {tribes.slice(0, 15).map((t, i) => <Cell key={t.tribe} fill={t.tribe === 'Not recorded' ? C.muted : tribeColors()[i % tribeColors().length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <DataTable rows={tribes} rowKey={(r) => r.tribe} maxHeight={390} fileName="registered-by-tribe" searchPlaceholder="Search tribe…"
                columns={[
                  { key: 'tribe', label: 'Tribe', value: (r) => r.tribe },
                  { key: 'registered', label: 'Registered', value: (r) => r.registered, kind: 'num', color: C.green },
                  { key: 'share', label: 'Share', value: (r) => (tribeTotal ? (r.registered / tribeTotal) * 100 : 0), kind: 'percent', total: () => 100 },
                ]} />
            )}
          </Panel>
        </Grid>
        {/* 3. Summary table */}
        <Grid item xs={12} lg={7}>
          <Panel title={`Summary by ${IEBC_LABELS[level].toLowerCase()}`} sx={{ height: 480 }}
            actions={level === 'pollingCentre' ? <Typography sx={{ fontSize: '0.65rem', color: C.muted }}>Sex and age are recorded per ward — see the cards above</Typography> : undefined}>
            <Loading on={reg.fetching} />
            <DataTable rows={rows} columns={columns} rowKey={(r) => r.key} onRowClick={f.pollingCentre || (level === 'pollingCentre' && !f.ward) ? undefined : (r) => drill(r.key)} maxHeight={390}
              fileName="registered-voters" searchPlaceholder={`Search ${IEBC_LABELS[level].toLowerCase()}…`} emptyText={reg.loading ? 'Loading…' : 'No rows'} />
          </Panel>
        </Grid>
      </Grid>
      </>)}
    </Section>
  );
};
